import React, { useState } from 'react';
import Input from '../../UI/Input';
import Button from '../../UI/Button';
import AISuggestionModal from '../AISuggestionModal';

export default function ExperienceForm({ experiences = [], onChange }) {
	const [aiIdx, setAiIdx] = useState(null);
	const items = experiences || [];

	function add() {
		onChange([...(items || []), { company: '', position: '', startDate: '', endDate: '', current: false, location: '', description: '', achievements: [] }]);
	}
	function remove(i) {
		onChange(items.filter((_, idx) => idx !== i));
	}
	function move(i, dir) {
		const j = i + dir;
		if (j < 0 || j >= items.length) return;
		const next = [...items];
		[next[i], next[j]] = [next[j], next[i]];
		onChange(next);
	}
	function update(i, key, value) {
		const next = [...items];
		next[i] = { ...next[i], [key]: value };
		if (key === 'current' && value) next[i].endDate = '';
		onChange(next);
	}
	function addAchievement(i, text) {
		const next = [...items];
		const arr = Array.isArray(next[i].achievements) ? next[i].achievements : [];
		next[i].achievements = [...arr, text];
		onChange(next);
	}
	function removeAchievement(i, idx) {
		const next = [...items];
		next[i].achievements = (next[i].achievements || []).filter((_, k) => k !== idx);
		onChange(next);
	}

	return (
		<div className="space-y-4">
			{!items.length && <div className="text-sm text-gray-500">No experience added yet.</div>}
			{items.map((exp, i) => {
				const duration = calcDuration(exp.startDate, exp.endDate, exp.current);
				return (
					<div key={i} className="border rounded-md p-4 space-y-3">
						<div className="flex items-center justify-between">
							<div className="text-sm text-gray-500">{duration ? `~ ${duration}` : ''}</div>
							<div className="flex items-center gap-2">
								<Button variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>
									Up
								</Button>
								<Button variant="ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1}>
									Down
								</Button>
								<Button variant="ghost" onClick={() => remove(i)}>
									Remove
								</Button>
							</div>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
							<Input name="company" label="Company Name" value={exp.company || ''} onChange={(e) => update(i, 'company', e.target.value)} required />
							<Input name="position" label="Position / Job Title" value={exp.position || ''} onChange={(e) => update(i, 'position', e.target.value)} required />
							<Input name="location" label="Location" value={exp.location || ''} onChange={(e) => update(i, 'location', e.target.value)} />
							<div className="grid grid-cols-2 gap-3">
								<Input name="startDate" label="Start (YYYY-MM)" value={exp.startDate || ''} onChange={(e) => update(i, 'startDate', e.target.value)} />
								<Input
									name="endDate"
									label="End (YYYY-MM)"
									value={exp.endDate || ''}
									onChange={(e) => update(i, 'endDate', e.target.value)}
									disabled={exp.current}
								/>
							</div>
							<label className="flex items-center gap-2 text-sm">
								<input type="checkbox" checked={!!exp.current} onChange={(e) => update(i, 'current', e.target.checked)} />
								Currently working here
							</label>
							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
								<textarea
									className="w-full rounded-md border border-gray-300 px-3 py-2"
									rows="3"
									value={exp.description || ''}
									onChange={(e) => update(i, 'description', e.target.value)}
								/>
								<div className="mt-2">
									<Button variant="secondary" type="button" onClick={() => setAiIdx(i)}>
										Improve with AI
									</Button>
								</div>
							</div>
							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-gray-700 mb-1">Achievements (bullet points)</label>
								<Bullets
									items={exp.achievements || []}
									onAdd={(text) => addAchievement(i, text)}
									onRemove={(idx) => removeAchievement(i, idx)}
								/>
							</div>
						</div>
					</div>
				);
			})}
			<Button variant="ghost" onClick={add}>
				Add Experience
			</Button>
			<AISuggestionModal
				isOpen={aiIdx !== null}
				onClose={() => setAiIdx(null)}
				sectionType="experience"
				promptContext={{
					position: items[aiIdx || 0]?.position || '',
					currentDescription: items[aiIdx || 0]?.description || ''
				}}
				onSelect={(text) => {
					// Split text by linebreaks into bullets
					const bullets = String(text)
						.split('\n')
						.map((s) => s.trim())
						.filter(Boolean);
					const next = [...items];
					next[aiIdx] = { ...next[aiIdx], achievements: bullets };
					onChange(next);
				}}
			/>
		</div>
	);
}

function Bullets({ items, onAdd, onRemove }) {
	const [val, setVal] = useState('');
	function submit(e) {
		e.preventDefault();
		if (!val.trim()) return;
		onAdd(val.trim());
		setVal('');
	}
	return (
		<div>
			<ul className="list-disc ml-5 space-y-1">
				{items.map((b, i) => (
					<li key={i} className="flex items-center gap-2">
						<span className="flex-1">{b}</span>
						<button className="text-sm text-red-600" type="button" onClick={() => onRemove(i)}>
							Remove
						</button>
					</li>
				))}
			</ul>
			<form className="mt-2 flex gap-2" onSubmit={submit}>
				<input
					className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
					placeholder="Add achievement and press Enter"
					value={val}
					onChange={(e) => setVal(e.target.value)}
				/>
				<Button type="submit" variant="ghost">
					Add
				</Button>
			</form>
		</div>
	);
}

function calcDuration(start, end, current) {
	if (!start) return '';
	const s = toYM(start);
	const e = current ? toYM(new Date()) : toYM(end || new Date());
	if (!s || !e) return '';
	let months = (e.y - s.y) * 12 + (e.m - s.m);
	if (months < 0) months = 0;
	const yrs = Math.floor(months / 12);
	const mos = months % 12;
	const parts = [];
	if (yrs) parts.push(`${yrs} yr${yrs > 1 ? 's' : ''}`);
	if (mos) parts.push(`${mos} mo${mos > 1 ? 's' : ''}`);
	return parts.join(' ');
}
function toYM(v) {
	try {
		const d = typeof v === 'string' ? new Date(v) : v;
		return { y: d.getFullYear(), m: d.getMonth() + 1 };
	} catch {
		return null;
	}
}


